import db from './offlineDb'

// Generate a unique temporary ID for offline-created entities
export const generateTempId = () =>
  `temp_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`

// Enqueue a mutation to the sync queue
export const enqueueSyncAction = async ({ type, payload, tempId = null, offlineFileId = null }) => {
  const entry = {
    type,
    payload,
    tempId,
    offlineFileId,
    status: 'pending',
    createdAt: new Date().toISOString(),
    retryCount: 0,
    lastError: null
  }
  const id = await db.syncQueue.add(entry)
  return id
}

// Get all pending sync entries (includes 'syncing' entries that were interrupted by crash)
export const getPendingSyncEntries = async () => {
  return db.syncQueue
    .where('status').anyOf('pending', 'syncing')
    .sortBy('createdAt')
}

// Update sync entry status
export const updateSyncEntryStatus = async (id, status, error = null) => {
  const updates = { status }
  if (error) updates.lastError = error
  if (status === 'pending') {
    const entry = await db.syncQueue.get(id)
    updates.retryCount = (entry?.retryCount || 0) + 1
  }
  await db.syncQueue.update(id, updates)
}

// Delete completed and failed sync entries
export const clearCompletedSyncEntries = async () => {
  await db.syncQueue.where('status').anyOf('completed', 'failed').delete()
}

// Replace temp IDs with real IDs across IDB
export const replaceTempId = async (tempId, realId) => {
  // Update tasks table
  const task = await db.tasks.get(tempId)
  if (task) {
    await db.tasks.delete(tempId)
    await db.tasks.put({ ...task, id: realId, _offline: false })
  }

  // Update pending files referencing this temp task ID
  const pendingFiles = await db.pendingFiles.where('taskId').equals(tempId).toArray()
  for (const file of pendingFiles) {
    await db.pendingFiles.update(file.id, { taskId: realId })
  }

  // Update other sync queue entries that reference this temp ID
  const queueEntries = await db.syncQueue.where('status').equals('pending').toArray()
  for (const entry of queueEntries) {
    if (entry.payload?.taskId === tempId) {
      await db.syncQueue.update(entry.id, {
        payload: { ...entry.payload, taskId: realId }
      })
    }
    if (entry.tempId === tempId) {
      await db.syncQueue.update(entry.id, { tempId: realId })
    }
  }
}


// Deduplicate: remove older pending entries of same type+task before adding new one
export const deduplicateSyncEntry = async (type, taskId) => {
  const existing = await db.syncQueue
    .where('status').equals('pending')
    .filter(e => e.type === type && e.payload?.taskId === taskId)
    .toArray()
  if (existing.length > 0) {
    await db.syncQueue.bulkDelete(existing.map(e => e.id))
  }
}

// Clean up orphaned pendingFiles and sync entries when a task is deleted offline
export const cleanupOrphanedFiles = async (taskId) => {
  await db.pendingFiles.where('taskId').equals(taskId).delete()
  // Remove all pending sync queue entries referencing this task
  const relatedEntries = await db.syncQueue
    .where('status').anyOf('pending', 'syncing')
    .filter(e => e.payload?.taskId === taskId || e.payload === taskId)
    .toArray()
  if (relatedEntries.length > 0) {
    await db.syncQueue.bulkDelete(relatedEntries.map(e => e.id))
  }
}
// Get current storage usage info
export const getStorageEstimate = async () => {
  if (!navigator.storage?.estimate) return null
  const { usage, quota } = await navigator.storage.estimate()
  const usedMb = Math.round(usage / (1024 * 1024))
  const totalMb = Math.round(quota / (1024 * 1024))
  const percentUsed = quota > 0 ? Math.round((usage / quota) * 100) : 0
  return { usage, quota, usedMb, totalMb, percentUsed }
}

// Check storage quota before adding files
export const checkStorageQuota = async (fileSize) => {
  if (navigator.storage?.estimate) {
    const { usage, quota } = await navigator.storage.estimate()
    const available = quota - usage
    const SAFETY_MARGIN = 50 * 1024 * 1024 // keep 50MB free
    const percentUsed = quota > 0 ? Math.round((usage / quota) * 100) : 0

    if (fileSize > available - SAFETY_MARGIN) {
      throw new Error(
        `Offline storage is full (${Math.round(usage / (1024 * 1024))}MB used). ` +
        'Please connect to the internet to sync your changes and free up space.'
      )
    }

    // Return usage info for proactive warnings
    return { percentUsed, usedMb: Math.round(usage / (1024 * 1024)) }
  }
  return null
}

// Store a pending file blob in IndexedDB
export const storePendingFile = async ({ taskId, fieldKey, fileName, contentType, blob, offlineFileId }) => {
  await checkStorageQuota(blob.size)
  return db.pendingFiles.add({
    taskId,
    fieldKey,
    fileName,
    contentType,
    blob,
    offlineFileId,
    status: 'pending',
    createdAt: new Date().toISOString()
  })
}

// Client-side task filtering (replaces server-side when offline)
export const applyTaskFilters = (tasks, filters) => {
  let filtered = [...tasks]

  if (filters.status && filters.status !== 'all') {
    filtered = filtered.filter(t => t.status === filters.status)
  }
  if (filters.assignedTo) {
    filtered = filtered.filter(t =>
      t.assignees?.some(a => a.id === filters.assignedTo)
    )
  }
  if (filters.approverId) {
    filtered = filtered.filter(t =>
      t.approvers?.some(a => a.id === filters.approverId)
    )
  }
  if (filters.open) {
    filtered = filtered.filter(t => t.status !== 'completed' && t.status !== 'on_hold')
  }
  if (filters.buildingId) {
    filtered = filtered.filter(t => t.buildingPassportId === filters.buildingId)
  }
  if (filters.projectId) {
    filtered = filtered.filter(t => t.projectId === filters.projectId)
  }
  if (filters.priority) {
    filtered = filtered.filter(t => t.priority === filters.priority)
  }
  if (filters.formTemplateId) {
    filtered = filtered.filter(t => t.formTemplateId === filters.formTemplateId)
  }
  if (filters.teamId) {
    filtered = filtered.filter(t => t.teamId === filters.teamId)
  }
  if (filters.search) {
    const q = filters.search.toLowerCase()
    filtered = filtered.filter(t =>
      t.title?.toLowerCase().includes(q) ||
      t.description?.toLowerCase().includes(q) ||
      (t.buildingPassport?.name || t.building?.name || '').toLowerCase().includes(q) ||
      (t.project?.name || '').toLowerCase().includes(q)
    )
  }
  if (filters.deadlineFrom) {
    filtered = filtered.filter(t => t.deadline >= filters.deadlineFrom)
  }
  if (filters.deadlineTo) {
    filtered = filtered.filter(t => t.deadline <= filters.deadlineTo)
  }
  if (filters.category) {
    filtered = filtered.filter(t => matchesTaskCategory(t, filters.category, filters.currentUserId))
  }

  return filtered
}

const isOpenTask = t => t.status !== 'completed'
const isMine = (t, userId) => !!userId && (t.assignees || []).some(a => String(a.id) === String(userId))
const deadlineTime = t => (t.deadline ? new Date(t.deadline).getTime() : null)

export const matchesTaskCategory = (task, category, currentUserId, now = new Date()) => {
  const deadline = deadlineTime(task)
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const endOfToday = startOfToday + 24 * 60 * 60 * 1000 - 1
  const isOverdue = deadline !== null && deadline < now.getTime() && isOpenTask(task)
  if (category.startsWith('project_')) return String(task.project?.id ?? task.taskProjectId) === category.replace('project_', '')
  switch (category) {
    case 'my_tasks': return isMine(task, currentUserId)
    case 'starred': return !!task.isStarred
    case 'today': return isOpenTask(task) && deadline !== null && deadline >= startOfToday && deadline <= endOfToday
    case 'due_this_week': return isOpenTask(task) && deadline !== null && deadline > endOfToday && deadline <= endOfToday + 7 * 24 * 60 * 60 * 1000
    case 'overdue': return isOverdue
    case 'my_overdue': return isOverdue && isMine(task, currentUserId)
    case 'awaiting_approval':
    case 'on_hold':
    case 'completed':
      return task.status === category
    case 'completed_late':
      return task.status === 'completed' && !!task.completedAt && deadline !== null && new Date(task.completedAt).getTime() > deadline
    default: return true
  }
}
